import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Sparkles, Send, ArrowLeft, KeyRound } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTelegram } from '@/hooks/useTelegram';

// Разрешённые российские почтовые домены (требование ФЗ-406: вход только через
// росномер/Госуслуги/ЕБС/российский сервис/российскую почту). Иностранные (Gmail и др.) — нельзя.
const RU_EMAIL_DOMAINS = [
  'yandex.ru', 'ya.ru', 'mail.ru', 'bk.ru', 'list.ru', 'inbox.ru',
  'internet.ru', 'rambler.ru', 'vk.com', 'xmail.ru',
];

function isRussianEmail(email: string): boolean {
  const domain = (email.split('@')[1] || '').trim().toLowerCase();
  if (!domain) return false;
  if (domain.endsWith('.ru') || domain.endsWith('.рф') || domain.endsWith('.su')) return true;
  return RU_EMAIL_DOMAINS.includes(domain);
}

export default function Login() {
  const { haptic } = useTelegram();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validEmailFormat(e: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!validEmailFormat(value)) {
      setError('Введи корректный email');
      return;
    }
    if (!isRussianEmail(value)) {
      setError('Вход только по российской почте (например @yandex.ru, @mail.ru). Иностранные почты (Gmail и др.) не поддерживаются по закону РФ.');
      haptic('error');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: value,
        options: { shouldCreateUser: true },
      });
      if (error) throw error;
      setStep('code');
      haptic('success');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Не удалось отправить код');
      haptic('error');
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    const token = code.trim();
    if (token.length < 6) {
      setError('Введи 6-значный код из письма');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token,
        type: 'email',
      });
      if (error) throw error;
      haptic('success');
      // Дальше useAuth поймает событие входа, создаст профиль и покажет онбординг/Дашборд.
    } catch (err: any) {
      console.error(err);
      setError('Неверный или просроченный код. Проверь цифры или запроси новый.');
      haptic('error');
    } finally {
      setLoading(false);
    }
  }

  function resetToEmail() {
    setStep('email');
    setCode('');
    setError(null);
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-8 max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full"
      >
        {/* Шапка */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-glow to-sky-glow flex items-center justify-center">
            <Sparkles size={28} className="text-bg" />
          </div>
        </div>
        <h1 className="font-display text-3xl font-bold text-center mb-1">Slava Health</h1>
        <p className="text-center text-sm text-ink-muted mb-1">
          Помощник по питанию и здоровью
        </p>
        <p className="text-center text-ink-muted mb-1 leading-relaxed text-sm">
          Питание · Привычки · Психология
        </p>
        <p className="text-center text-xs text-ink-dim mb-10">by Kokorin Health</p>

        {/* TODO (шаги 2-3): сюда добавим кнопки «Войти через VK ID» и «Яндекс ID» */}

        {step === 'email' ? (
          <motion.form
            key="email"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={sendCode}
            className="space-y-3"
          >
            <div>
              <label className="text-xs text-ink-muted mb-1.5 block">Российская почта</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim" />
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="input pl-10"
                  placeholder="ivan@yandex.ru"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(null); }}
                  disabled={loading}
                  autoFocus
                />
              </div>
              {error && <p className="text-xs text-danger mt-2 leading-relaxed">{error}</p>}
            </div>
            <button
              type="submit"
              disabled={!email || loading}
              className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-bg border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={16} />
              )}
              {loading ? 'Отправляем…' : 'Получить код'}
            </button>
            <p className="text-center text-[11px] text-ink-dim leading-relaxed px-2">
              Вход по российской почте (@yandex.ru, @mail.ru и т.п.). На неё придёт код для входа.
            </p>
          </motion.form>
        ) : (
          <motion.form
            key="code"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={verifyCode}
            className="space-y-3"
          >
            <div className="text-center mb-2">
              <div className="w-14 h-14 rounded-full bg-emerald-glow/15 flex items-center justify-center mx-auto mb-3">
                <KeyRound size={26} className="text-emerald-glow" />
              </div>
              <p className="text-sm text-ink-muted leading-relaxed">
                Отправили код на <span className="text-ink font-medium">{email}</span>
              </p>
            </div>
            <div>
              <label className="text-xs text-ink-muted mb-1.5 block">Код из письма</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="input text-center text-2xl tracking-[0.4em] font-mono"
                placeholder="______"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(null); }}
                disabled={loading}
                autoFocus
              />
              {error && <p className="text-xs text-danger mt-2 leading-relaxed">{error}</p>}
            </div>
            <button
              type="submit"
              disabled={code.length < 6 || loading}
              className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-bg border-t-transparent rounded-full animate-spin" />
              ) : null}
              {loading ? 'Проверяем…' : 'Войти'}
            </button>
            <button
              type="button"
              onClick={resetToEmail}
              className="w-full text-[11px] text-ink-dim hover:text-ink py-1 flex items-center justify-center gap-1"
            >
              <ArrowLeft size={12} /> Изменить почту
            </button>
            <p className="text-center text-[11px] text-ink-dim leading-relaxed">
              Не пришло письмо? Проверь папку «Спам». Код действует ограниченное время.
            </p>
          </motion.form>
        )}

        <div className="text-center mt-8 text-[11px] text-ink-dim leading-relaxed px-4">
          Продолжая, ты соглашаешься с условиями сервиса
        </div>
      </motion.div>
    </div>
  );
}
